import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Shared Supabase Storage helpers.
 *
 * Extracted so upload surfaces stop reaching into each other: avatars live in
 * profile.ts and slide images in archives.ts, but both need the same three
 * primitives, and profile.ts was importing them from archives.ts. Every future
 * upload surface needs the same, so they belong in one place neither owns.
 *
 * The bucket is PUBLIC, so CDN reads bypass RLS. Anything not explicitly
 * deleted stays fetchable at its URL forever, including images a user believes
 * they removed. Abandoning an upload is a privacy bug, not just a cost one:
 * every write path must delete what it supersedes.
 */
export const ASSET_BUCKET = 'el-portal-assets';

/**
 * Storage columns hold either a bare object path or a full public URL, because
 * read paths rewrite paths to public URLs and clients hand that value straight
 * back on save. Normalise to the path so it can be deleted.
 * Returns '' for anything that is not an object in our bucket.
 */
export function storagePathFromValue(value: string | null | undefined): string {
    if (!value) return '';
    const noQuery = value.split('?')[0];
    const marker = `/${ASSET_BUCKET}/`;
    const at = noQuery.indexOf(marker);
    if (at !== -1) return noQuery.slice(at + marker.length);
    return noQuery.startsWith('http') ? '' : noQuery;
}

/**
 * Delete a superseded object, best effort. A failed cleanup must never fail the
 * save the user actually asked for: the worst case is one orphan, recoverable
 * with tools/storage-orphans.mjs, whereas throwing here would lose their edit.
 * Never call with the path that is about to be written.
 */
export async function removeStorageObject(client: SupabaseClient, path: string): Promise<void> {
    if (!path) return;
    const { error } = await client.storage.from(ASSET_BUCKET).remove([path]);
    if (error) console.warn(`[storage] could not delete superseded object ${path}: ${error.message}`);
}
